import {TicketEntity} from "@dropDesk/domain/entities/ticket/ticket.entity";
import {TicketConfig, TicketConfigType} from "@dropDesk/domain/entities/ticket/ticket_config";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";
import {ClientEntity} from "@dropDesk/domain/entities/client/client.entity";
import {SectorEntity} from "@dropDesk/domain/entities/sector/sector.entity";
import {
    TicketsDescriptionStaticEntity
} from "@dropDesk/domain/entities/ticket_description_static/ticket_description_static.entity";
import {UserRole} from "@dropDesk/domain/entities/user/user_enum";
import {removeDiacritics} from "@dropDesk/utils/helpers/string_helper";
import {TicketStatusSubType, TicketStatusType} from "@dropDesk/domain/entities/ticket/ticket_maps";

export type TicketListFilterContext = {
    searchParam: string;
    listOnlyDeleted: boolean;
    filterStatus: TicketConfig | null;
    filterPriority: TicketConfig | null;
    filterClient: ClientEntity | null;
    filterAttendant: UserEntity | null;
    filterUserClient: UserEntity | null;
    filterSector: SectorEntity | null;
    filterDescriptionStatic: TicketsDescriptionStaticEntity | null;
    listOnlySectorEmpty: boolean;
    period: Date[] | null;
    loggedUser: UserEntity | null;
    ticketStatusConfigs: TicketConfig[];
};

export const getRealStatusList = (statusId: string, ticketStatusConfigs: TicketConfig[]): string[] => {
    const selected = ticketStatusConfigs.find((config) => config.id === statusId);
    if (selected?.isDefaultClosedStatus) {
        return ticketStatusConfigs
            .filter((config) =>
                config.type === TicketConfigType.status &&
                (
                    config.extra?.type === TicketStatusType.closed ||
                    config.extra?.subType === TicketStatusSubType.byWaitingReview
                )
            )
            .map((config) => config.id);
    }
    return [statusId];
};

const isNumericSearch = (value: string): boolean => {
    return /^\d+$/.test(value.trim());
};

const ticketHasUser = (ticket: TicketEntity, userId: string): boolean => {
    return !!ticket.users?.[userId];
};

export const hydrateTicketStatusObject = (
    ticket: TicketEntity,
    ticketStatusConfigs: TicketConfig[],
): TicketEntity => {
    if (ticket.statusObject?.extra?.type) {
        return ticket;
    }
    const statusObject = ticketStatusConfigs.find((config) => config.id === ticket.status);
    if (!statusObject) {
        return ticket;
    }
    return ticket.copyWith({statusObject});
};

const isTicketFromMySector = (ticket: TicketEntity, user: UserEntity): boolean => {
    return (user.sectors ?? []).some((entry) => entry.idSector === ticket.idSector);
};

const ticketMatchesListPermission = (
    ticket: TicketEntity,
    user: UserEntity,
    listOnlySectorEmpty: boolean,
): boolean => {
    if (!user.isAttendant) {
        return ticketHasUser(ticket, user.id);
    }

    if (listOnlySectorEmpty && user.permissionAdmin) {
        return !ticket.idSector;
    }

    const isOpenWithoutSector = ticket.hasOpenStatus && !ticket.idSector;

    if (user.userMain) {
        return !!ticket.idSector || isOpenWithoutSector;
    }

    if (user.permissionAdmin) {
        return isTicketFromMySector(ticket, user) || isOpenWithoutSector;
    }

    if (user.extraInfo?.permissionJustListUserTicket) {
        return (ticket.hasOpenStatus && isTicketFromMySector(ticket, user))
            || ticketHasUser(ticket, user.id);
    }

    return isTicketFromMySector(ticket, user);
};

const normalizeForSearch = (value: string): string => {
    return removeDiacritics(value.trim().toLowerCase());
};

const getTicketUserClients = (ticket: TicketEntity): UserEntity[] => {
    return (ticket.usersInfo ?? []).filter((user) => user.role === UserRole.userClient);
};

const userClientMatchesSearch = (user: UserEntity, search: string): boolean => {
    const term = normalizeForSearch(search);
    const name = normalizeForSearch(user.name ?? '');
    if (name.includes(term)) {
        return true;
    }
    const searchDigits = search.replace(/\D/g, '');
    const phone = (user.telephone ?? '').replace(/\D/g, '');
    if (searchDigits.length >= 3 && phone.includes(searchDigits)) {
        return true;
    }
    return false;
};

const ticketMatchesUserClientSearch = (ticket: TicketEntity, search: string): boolean => {
    return getTicketUserClients(ticket).some((user) => userClientMatchesSearch(user, search));
};

const ticketMatchesSearch = (ticket: TicketEntity, ctx: TicketListFilterContext): boolean => {
    const search = (ctx.searchParam ?? '').trim();
    if (!search) {
        return true;
    }
    if (isNumericSearch(search)) {
        if (ticket.number === parseInt(search, 10)) {
            return true;
        }
        if (search.length >= 3) {
            return ticketMatchesUserClientSearch(ticket, search);
        }
        return false;
    }
    if (search.length < 3) {
        return true;
    }
    return ticketMatchesUserClientSearch(ticket, search);
};

const ticketMatchesPeriod = (ticket: TicketEntity, period: Date[] | null): boolean => {
    if (!period || period.length < 2 || !ticket.createdAt) {
        return true;
    }
    const createdAt = new Date(ticket.createdAt).getTime();
    const gte = period[0].getTime();
    const lte = period[1].getTime();
    return createdAt >= gte && createdAt <= lte;
};

export const ticketMatchesListFilters = (ticket: TicketEntity, ctx: TicketListFilterContext): boolean => {
    const deleted = ticket.deleted === true;
    if (ctx.listOnlyDeleted !== deleted) {
        return false;
    }

    if (!ctx.loggedUser) {
        return false;
    }

    if (!ticketMatchesListPermission(ticket, ctx.loggedUser, ctx.listOnlySectorEmpty)) {
        return false;
    }

    if (ctx.filterClient && ticket.idClient !== ctx.filterClient.id) {
        return false;
    }

    if (ctx.filterUserClient && !ticketHasUser(ticket, ctx.filterUserClient.id)) {
        return false;
    }

    if (ctx.filterAttendant && !ticketHasUser(ticket, ctx.filterAttendant.id)) {
        return false;
    }

    if (ctx.filterSector && ticket.idSector !== ctx.filterSector.id) {
        return false;
    }

    if (ctx.listOnlySectorEmpty && ticket.idSector) {
        return false;
    }

    if (ctx.filterStatus) {
        const allowed = getRealStatusList(ctx.filterStatus.id, ctx.ticketStatusConfigs);
        const ticketStatus = ticket.status ?? ticket.statusObject?.id;
        if (!ticketStatus || !allowed.includes(ticketStatus)) {
            return false;
        }
    }

    if (ctx.filterPriority && ticket.priority !== ctx.filterPriority.id) {
        return false;
    }

    if (ctx.filterDescriptionStatic && ticket.idDescriptionStatic !== ctx.filterDescriptionStatic.id) {
        return false;
    }

    if (!ticketMatchesPeriod(ticket, ctx.period)) {
        return false;
    }

    if (!ticketMatchesSearch(ticket, ctx)) {
        return false;
    }

    return true;
};

export const mergeTicketFields = (existing: TicketEntity, incoming: TicketEntity): TicketEntity => {
    return existing.copyWith({
        idSector: incoming.idSector,
        idClient: incoming.idClient,
        dateDue: incoming.dateDue,
        description: incoming.description,
        idDescriptionStatic: incoming.idDescriptionStatic,
        number: incoming.number,
        timeSpent: incoming.timeSpent,
        timeFirstResponse: incoming.timeFirstResponse,
        origin: incoming.origin,
        originDetail: incoming.originDetail,
        users: incoming.users,
        updatedAt: incoming.updatedAt,
        updateOp: incoming.updateOp,
        lastMessage: incoming.lastMessage,
        status: incoming.status,
        priority: incoming.priority,
        resolutionTime: incoming.resolutionTime,
        createdAt: incoming.createdAt,
        dateClosed: incoming.dateClosed,
        deleted: incoming.deleted,
        descriptionClosed: incoming.descriptionClosed,
        statusObject: incoming.statusObject ?? existing.statusObject,
        priorityObject: incoming.priorityObject ?? existing.priorityObject,
        lastMessageId: incoming.lastMessageId,
        countUnreadNotification: incoming.countUnreadNotification,
        chatOperations: incoming.chatOperations,
        usersInfo: incoming.usersInfo ?? existing.usersInfo,
        client: incoming.client ?? existing.client,
        sector: incoming.sector ?? existing.sector,
        descriptionStatic: incoming.descriptionStatic ?? existing.descriptionStatic,
    });
};

export type MergeTicketIntoPageResult = {
    data: TicketEntity[];
    inserted: boolean;
    updated: boolean;
};

export const mergeTicketIntoPage = (
    data: TicketEntity[],
    incoming: TicketEntity,
    limit: number,
): MergeTicketIntoPageResult => {
    const index = data.findIndex((entry) => entry.id === incoming.id);
    if (index >= 0) {
        const next = [...data];
        next[index] = mergeTicketFields(data[index], incoming);
        return {data: next, inserted: false, updated: true};
    }

    const next = [...data, incoming].sort((a, b) => b.number - a.number);
    if (next.length > limit) {
        next.pop();
    }
    return {data: next, inserted: true, updated: false};
};

export const removeTicketFromPage = (data: TicketEntity[], ticketId: string): TicketEntity[] => {
    return data.filter((entry) => entry.id !== ticketId);
};

export const recalcPages = (totalRows: number, limit: number): number => {
    if (limit <= 0) {
        return 0;
    }
    return Math.ceil(totalRows / limit);
};
